/**
 * F31 (Wave 2B) — admin rating-override dialog (Decision 15).
 *
 * super_admin only (the parent page gates the trigger button on
 * `skill_ratings:override`). The new effective value may exceed 5.0 (unlike the
 * user-side claimed-value cap) but stays within [1.0, 7.0]. Mandatory reason
 * ≥10 chars, validated against `AdminRatingOverrideInputSchema`. A persistent
 * warning Banner makes the audited nature explicit.
 *
 * Composes F02 primitives only (Modal / FormField / NumberInput / Textarea /
 * Button / Banner). Posts via the parent's `onOverride`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, NumberInput, Textarea } from '@bravemove/ui-web';
import {
  AdminRatingOverrideInputSchema,
  ELO_MAX_RATING,
  ELO_MIN_RATING,
  type AdminRatingOverrideInput,
} from '@bravemove/types';
import type { SportName } from '@bravemove/design-tokens/icons';

export interface RatingOverrideDialogProps {
  readonly open: boolean;
  readonly sport: SportName;
  readonly currentValue: number;
  readonly onOverride: (input: AdminRatingOverrideInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function RatingOverrideDialog({
  open,
  sport,
  currentValue,
  onOverride,
  onClose,
  errorMessage,
}: RatingOverrideDialogProps) {
  const { t } = useI18n();
  const [newValue, setNewValue] = useState(currentValue);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setNewValue(currentValue);
    setReason('');
  }, [open, currentValue]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: AdminRatingOverrideInput = useMemo(
    () => ({ newEffectiveSkillValue: newValue, reason }),
    [newValue, reason],
  );
  const valid = useMemo(() => AdminRatingOverrideInputSchema.safeParse(payload).success, [payload]);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onOverride(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('skill:admin.rating_override.title', { defaultValue: 'Override rating' })}
      description={sport.replace(/-/g, ' ')}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="rating-override-cancel"
          >
            {t('skill:outcome.dialog.confirm.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="rating-override-submit"
          >
            {t('skill:admin.rating_override.submit', { defaultValue: 'Override rating' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="rating-override-dialog">
        <div data-testid="rating-override-warning">
          <Banner tone="warning">
            {t('skill:admin.rating_override.warning', {
              defaultValue:
                "This is an audited action. It overrides the player's computed rating and is logged with your name.",
            })}
          </Banner>
        </div>
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('skill:admin.rating_override.new_value_label', {
            defaultValue: 'New skill value',
          })}
          required
        >
          <NumberInput
            value={newValue}
            min={ELO_MIN_RATING}
            max={ELO_MAX_RATING}
            step={0.1}
            onChange={(n) => setNewValue(Math.round(n * 10) / 10)}
            data-testid="rating-override-value"
          />
        </FormField>
        <FormField
          label={t('skill:admin.rating_override.reason_label', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('skill:admin.rating_override.reason_placeholder', {
              defaultValue: 'Why are you overriding this rating? (at least 10 characters)',
            })}
            rows={4}
            maxLength={500}
            data-testid="rating-override-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
